/**
 * 注册页面 - 参考 openlink.chat 设计风格（带邮箱验证）
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { register as registerApi } from '../../api/auth';
import { sendVerificationCode } from '../../api/verification';
import { toast } from 'sonner';

const registerSchema = z.object({
  email: z.string().email('请输入有效的邮箱地址'),
  password: z
    .string()
    .min(8, '密码至少 8 位')
    .regex(/[A-Z]/, '密码需包含大写字母')
    .regex(/[a-z]/, '密码需包含小写字母')
    .regex(/[0-9]/, '密码需包含数字'),
  confirmPassword: z.string(),
  nickname: z.string().min(2, '昵称至少 2 位').max(20, '昵称最多 20 位').optional(),
  code: z.string().length(6, '验证码为 6 位数字'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "两次输入的密码不一致",
  path: ['confirmPassword'],
});

type RegisterForm = z.infer<typeof registerSchema>;

export default function Register() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isRegistered, setIsRegistered] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
  });

  const email = watch('email');

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSendCode = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      toast.error('请输入有效的邮箱地址');
      return;
    }

    setIsSendingCode(true);
    try {
      await sendVerificationCode({ email, purpose: 'register' });
      toast.success('验证码已发送，请查收邮箱');
      setIsCodeSent(true);
      setCountdown(60);
    } catch (error: any) {
      // 错误已在 axios 拦截器中处理
    } finally {
      setIsSendingCode(false);
    }
  };

  const onSubmit = async (data: RegisterForm) => {
    setIsLoading(true);
    try {
      await registerApi({
        email: data.email,
        password: data.password,
        nickname: data.nickname,
        code: data.code,
      });
      setIsRegistered(true);
      toast.success('注册成功！');
    } catch (error: any) {
      // 错误已在 axios 拦截器中处理
    } finally {
      setIsLoading(false);
    }
  };

  if (isRegistered) {
    return (
      <div className="min-h-screen bg-gray-50">
        <nav className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex items-center">
                <Link to="/" className="flex items-center space-x-2">
                  <img src="/logo.png" alt="Logo" className="w-[90px] h-auto" />
                </Link>
              </div>
            </div>
          </div>
        </nav>
        <main className="py-8">
          <div className="max-w-md mx-auto px-4 text-center">
            <div className="mb-6">
              <svg className="mx-auto w-16 h-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">注册成功</h1>
            <p className="text-gray-600 mb-6">您的账号已创建成功</p>
            <div className="bg-white rounded-lg shadow-md p-6 text-center mb-6">
              <p className="text-gray-700 mb-2">邮箱：{email}</p>
              <p className="text-sm text-gray-500">请使用该邮箱和密码登录系统</p>
            </div>
            <Link to="/login" className="block w-full bg-lobster-red text-white py-2 rounded-md hover:bg-lobster-red/90 font-medium">
              去登录 →
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航栏 */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-2">
                <img src="/logo.png" alt="Logo" className="w-[90px] h-auto" />
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/login" className="text-gray-600 hover:text-gray-900 text-sm">登录</Link>
              <span className="bg-lobster-red text-white px-4 py-2 rounded-md text-sm">注册</span>
            </div>
          </div>
        </div>
      </nav>

      {/* 主内容 */}
      <main className="py-8">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">创建账号</h2>
              <p className="text-gray-600 mt-2 text-sm">开始管理你的机器人</p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* 邮箱 + 验证码 */}
              <div>
                <label htmlFor="email" className="block text-gray-700 text-sm font-medium mb-2">邮箱</label>
                <div className="flex gap-2">
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    className={`flex-1 border px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-lobster-red ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    {...register('email')}
                    placeholder="your@email.com"
                    disabled={isCodeSent}
                  />
                  <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={isSendingCode || isCodeSent}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
                  >
                    {isSendingCode ? '发送中...' : isCodeSent ? `已发送 (${countdown}s)` : '获取验证码'}
                  </button>
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="code" className="block text-gray-700 text-sm font-medium mb-2">验证码</label>
                <input
                  id="code"
                  type="text"
                  className={`w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-lobster-red ${
                    errors.code ? 'border-red-500' : 'border-gray-300'
                  }`}
                  {...register('code')}
                  placeholder="6 位数字验证码"
                />
                {errors.code && (
                  <p className="mt-1 text-sm text-red-600">{errors.code.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="nickname" className="block text-gray-700 text-sm font-medium mb-2">昵称（可选）</label>
                <input
                  id="nickname"
                  type="text"
                  className={`w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-lobster-red ${
                    errors.nickname ? 'border-red-500' : 'border-gray-300'
                  }`}
                  {...register('nickname')}
                  placeholder="怎么称呼你"
                />
                {errors.nickname && (
                  <p className="mt-1 text-sm text-red-600">{errors.nickname.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-gray-700 text-sm font-medium mb-2">密码</label>
                <input
                  id="password"
                  type="password"
                  className={`w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-lobster-red ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  {...register('password')}
                  placeholder="至少 8 位，包含大小写字母和数字"
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-gray-700 text-sm font-medium mb-2">确认密码</label>
                <input
                  id="confirmPassword"
                  type="password"
                  className={`w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-lobster-red ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  {...register('confirmPassword')}
                  placeholder="再次输入密码"
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-lobster-red text-white py-2 rounded-md hover:bg-lobster-red/90 font-medium disabled:opacity-50"
              >
                {isLoading ? '注册中...' : '注册'}
              </button>
            </form>

            <p className="text-center text-gray-600 mt-6">
              已有账号？{' '}
              <Link to="/login" className="text-lobster-red hover:text-lobster-red/80 font-medium">
                立即登录
              </Link>
            </p>
          </div>
        </div>
      </main>

      {/* 页脚 */}
      <footer className="bg-white border-t mt-auto">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img src="/logo.png" alt="Logo" className="w-6 h-6" />
              <span className="text-sm text-gray-600">OpenBus 机器人互联平台</span>
            </div>
            <p className="text-sm text-gray-500">&copy; 2026 OpenBus. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
